import { getIframe, getSiteDomain } from '@/shared/iframeStore';
export default (config) => ({
    issuesVisibility: {},
    issues: [],
    receivedElements: false,
    _rematchListener: null,
    _rematchTimeout: null,
    _rematchedIds: new Set(),
    _isPostRematch: false,
    _visibilityRunId: 0,
    init() {
        window.Livewire.on("initIssuesVisibility", (e) => {
            this.issues = e.issues;
            this.receivedElements = false;
            if (this._isPostRematch) {
                this._isPostRematch = false;
                // Clear after current sync cycle so the filter still blocks re-loops
                // in this initIssuesVisibility() call, but IDs aren't locked permanently
                setTimeout(() => { this._rematchedIds.clear(); }, 0);
            } else {
                this._rematchedIds.clear();
            }
            this.initIssuesVisibility();
        });
        window.Livewire.on("updateIssuesVisibility", () => {
            this.receivedElements = false;
            this._rematchedIds.clear();
            this.initIssuesVisibility();
        });
        window.Livewire.on("newIssueCreated", (e) => {
            this.issues.push(e[0].issue_instance);
            this.receivedElements = false;
            this._rematchedIds.clear();
            this.initIssuesVisibility();
        });
        window.addEventListener("message", this.handleMessage.bind(this), true);
    },

    handleMessage(event) {
        let iframe = getIframe();
        let site_domain = getSiteDomain();
        if (!iframe || !site_domain) {
            // not ready yet
            return;
        }

        if (event.origin !== site_domain) {
            return;
        }
        if (event.data.msg === "updateIssueVisibility") {
            this.issuesVisibility[event.data.issue_id] = event.data.is_visible;
        }
    },

    async initIssuesVisibility() {
        const runId = ++this._visibilityRunId;

        if (!this.issues.length) {
            this.receivedElements = true;
            return;
        }
        setTimeout(() => {
            if (!this.receivedElements) {
                window.Livewire.dispatch("updateState", { data: {
                    state: "error",
                    reason: "could not get issues positions from iframe",
                }});
            }
        }, 4000);
        let iframe = getIframe();
        let site_domain = getSiteDomain();
        iframe.contentWindow.postMessage(
            {
                function: "passSelectors",
                selectors: JSON.stringify(this.issues),
            },
            site_domain
        );

        const brokenInstances = [];

        for (const issue of this.issues) {
            if (runId !== this._visibilityRunId) return; // superseded by newer call
            if (issue.selector) {
                iframe.contentWindow.postMessage(
                    {
                        function: "getIssueVisibility",
                        selector: issue.selector,
                    },
                    site_domain
                );

                const event = await new Promise((resolve) => {
                    const listener2 = (event) => {
                        if (event.origin === site_domain && event.data.msg === "getIssueVisibility") {
                            window.removeEventListener("message", listener2, false);
                            resolve(event);
                        }
                    };

                    window.addEventListener("message", listener2);
                });

                this.receivedElements = true;
                this.issuesVisibility[issue.id] = event.data.is_visible;

                if (event.data.element_found === false && issue.fingerprint) {
                    brokenInstances.push({ instance_id: issue.id, selector: issue.selector, fingerprint: issue.fingerprint });
                }
            } else {
                this.issuesVisibility[issue.id] = false;
                if (issue.fingerprint) {
                    brokenInstances.push({ instance_id: issue.id, selector: null, fingerprint: issue.fingerprint });
                }
            }
        }

        const newBrokenInstances = brokenInstances.filter(b => !this._rematchedIds.has(b.instance_id));
        if (newBrokenInstances.length > 0) {
            this.rematchByFingerprint(newBrokenInstances, iframe, site_domain);
        }
    },

    rematchByFingerprint(brokenInstances, iframe, site_domain) {
        // Cancel any in-flight rematch
        if (this._rematchListener) {
            window.removeEventListener('message', this._rematchListener);
            clearTimeout(this._rematchTimeout);
        }

        const listener = (event) => {
            if (event.origin !== site_domain) return;
            if (event.data.msg !== 'fingerprintMatchResults') return;
            window.removeEventListener('message', listener);
            clearTimeout(timeout);
            this._rematchListener = null;
            this._rematchTimeout = null;

            const updates = event.data.results;
            if (updates && updates.length > 0) {
                this._isPostRematch = true;
                window.Livewire.dispatch('updateInstanceSelectors', { updates });
            }
        };
        this._rematchListener = listener;
        window.addEventListener('message', listener);
        const timeout = setTimeout(() => {
            window.removeEventListener('message', listener);
            this._rematchListener = null;
            this._rematchTimeout = null;
        }, 10000);
        this._rematchTimeout = timeout;

        brokenInstances.forEach(b => this._rematchedIds.add(b.instance_id));

        iframe.contentWindow.postMessage(
            {
                function: 'findByFingerprint',
                instances: JSON.stringify(brokenInstances),
            },
            site_domain
        );
    },

    getIsIssueVisible(issueId) {
        return this.issuesVisibility[issueId];
    },
});
